export const VIDEO_BLOCK_TYPES = {
  Youtube: 'video:youtube',
  VideoTouch: 'video:video-touch',
} as const

export type VideoBlockType = (typeof VIDEO_BLOCK_TYPES)[keyof typeof VIDEO_BLOCK_TYPES]

export interface VideoBlockItem {
  type: VideoBlockType
  youtubeId?: string
  youtubeVideo?: string
  youtubeThumbnail?: string
  videoTouchId?: string
  videoTouchVideo?: string
  videoTouchThumbnail?: string
}

export type VideoBlockMarkdownSegment =
  | {
      type: 'markdown'
      markdown: string
    }
  | {
      type: 'videoBlock'
      video: VideoBlockItem
    }

const VIDEO_BLOCK_PATTERN = /\[\[video\b([^\]]*)\]\]/g
const VIDEO_BLOCK_HTML_PATTERN =
  /<video-block\b([^>]*)>\s*<\/video-block>|<video-block\b([^>]*)\/>/g
const VIDEO_BLOCK_PLACEHOLDER = 'KARAKURI_VIDEO_BLOCK'

export const createDefaultVideoBlockItem = (
  type: VideoBlockType = VIDEO_BLOCK_TYPES.Youtube
): VideoBlockItem => {
  if (type === VIDEO_BLOCK_TYPES.VideoTouch) {
    return {
      type,
      videoTouchId: '',
      videoTouchVideo: '',
      videoTouchThumbnail: '',
    }
  }

  return {
    type: VIDEO_BLOCK_TYPES.Youtube,
    youtubeId: '',
    youtubeVideo: '',
    youtubeThumbnail: '',
  }
}

export function normalizeVideoBlockItem(item: Partial<VideoBlockItem>): VideoBlockItem {
  if (item.type === VIDEO_BLOCK_TYPES.VideoTouch) {
    return {
      type: VIDEO_BLOCK_TYPES.VideoTouch,
      videoTouchId: item.videoTouchId?.trim() ?? '',
      videoTouchVideo: item.videoTouchVideo?.trim() ?? '',
      videoTouchThumbnail: item.videoTouchThumbnail?.trim() ?? '',
    }
  }

  return {
    type: VIDEO_BLOCK_TYPES.Youtube,
    youtubeId: item.youtubeId?.trim() ?? '',
    youtubeVideo: item.youtubeVideo?.trim() ?? '',
    youtubeThumbnail: item.youtubeThumbnail?.trim() ?? '',
  }
}

export function encodeVideoBlockItem(item: Partial<VideoBlockItem>): string {
  return encodeURIComponent(JSON.stringify(normalizeVideoBlockItem(item)))
}

export function decodeVideoBlockItem(encodedItem: string | undefined): VideoBlockItem {
  if (!encodedItem) {
    return createDefaultVideoBlockItem()
  }

  try {
    const parsed: unknown = JSON.parse(decodeURIComponent(encodedItem))
    if (!isVideoBlockItemLike(parsed)) {
      return createDefaultVideoBlockItem()
    }

    return normalizeVideoBlockItem(parsed)
  } catch {
    return createDefaultVideoBlockItem()
  }
}

export function serializeVideoBlockMarkdown(item: Partial<VideoBlockItem>): string {
  const video = normalizeVideoBlockItem(item)
  if (video.type === VIDEO_BLOCK_TYPES.VideoTouch) {
    return [
      '[[video',
      `type="${VIDEO_BLOCK_TYPES.VideoTouch}"`,
      `videoTouchId="${escapeShortcodeAttribute(video.videoTouchId ?? '')}"`,
      `videoTouchVideo="${escapeShortcodeAttribute(video.videoTouchVideo ?? '')}"`,
      `videoTouchThumbnail="${escapeShortcodeAttribute(video.videoTouchThumbnail ?? '')}"`,
      ']]',
    ].join(' ')
  }

  return [
    '[[video',
    `type="${VIDEO_BLOCK_TYPES.Youtube}"`,
    `youtubeId="${escapeShortcodeAttribute(video.youtubeId ?? '')}"`,
    `youtubeVideo="${escapeShortcodeAttribute(video.youtubeVideo ?? '')}"`,
    `youtubeThumbnail="${escapeShortcodeAttribute(video.youtubeThumbnail ?? '')}"`,
    ']]',
  ].join(' ')
}

export function splitVideoBlocksFromMarkdown(markdown: string): VideoBlockMarkdownSegment[] {
  const segments: VideoBlockMarkdownSegment[] = []
  let lastIndex = 0

  markdown.replace(
    VIDEO_BLOCK_PATTERN,
    (matchedText: string, attributes: string, offset: number) => {
      if (offset > lastIndex) {
        segments.push({ type: 'markdown', markdown: markdown.slice(lastIndex, offset) })
      }
      segments.push({ type: 'videoBlock', video: parseVideoBlockAttributes(attributes) })
      lastIndex = offset + matchedText.length
      return matchedText
    }
  )

  if (lastIndex < markdown.length) {
    segments.push({ type: 'markdown', markdown: markdown.slice(lastIndex) })
  }

  return segments.length > 0 ? segments : [{ type: 'markdown', markdown }]
}

export function replaceVideoBlockMarkdownWithPlaceholders(markdown: string): {
  markdown: string
  videos: VideoBlockItem[]
} {
  const videos: VideoBlockItem[] = []
  const replacedMarkdown = markdown.replace(
    VIDEO_BLOCK_PATTERN,
    (_matchedText, attributes: string) => {
      const index = videos.length
      videos.push(parseVideoBlockAttributes(attributes))
      return `${VIDEO_BLOCK_PLACEHOLDER}_${index}`
    }
  )

  return { markdown: replacedMarkdown, videos }
}

export function restoreVideoBlockHtmlPlaceholders(html: string, videos: VideoBlockItem[]): string {
  return videos.reduce((restoredHtml, video, index) => {
    const placeholder = `${VIDEO_BLOCK_PLACEHOLDER}_${index}`
    const videoBlockHtml = createVideoBlockHtml(video)
    return restoredHtml
      .replace(new RegExp(`<p>\\s*${placeholder}\\s*</p>`, 'g'), videoBlockHtml)
      .replace(new RegExp(placeholder, 'g'), videoBlockHtml)
  }, html)
}

export function replaceVideoBlockHtmlWithPlaceholders(html: string): {
  html: string
  videos: VideoBlockItem[]
} {
  const videos: VideoBlockItem[] = []
  const replacedHtml = html.replace(
    VIDEO_BLOCK_HTML_PATTERN,
    (_matchedText, attributesA: string | undefined, attributesB: string | undefined) => {
      const index = videos.length
      videos.push(decodeVideoBlockItem(readHtmlAttribute(attributesA ?? attributesB ?? '')))
      return `<p>${VIDEO_BLOCK_PLACEHOLDER}_${index}</p>`
    }
  )

  return { html: replacedHtml, videos }
}

export function restoreVideoBlockMarkdownPlaceholders(
  markdown: string,
  videos: VideoBlockItem[]
): string {
  return videos.reduce(
    (restoredMarkdown, video, index) =>
      restoredMarkdown.replace(
        createMarkdownPlaceholderRegExp(`${VIDEO_BLOCK_PLACEHOLDER}_${index}`),
        serializeVideoBlockMarkdown(video)
      ),
    markdown
  )
}

function parseVideoBlockAttributes(attributes: string): VideoBlockItem {
  const type = readShortcodeAttribute(attributes, 'type')
  if (type === VIDEO_BLOCK_TYPES.VideoTouch) {
    return normalizeVideoBlockItem({
      type,
      videoTouchId: readShortcodeAttribute(attributes, 'videoTouchId'),
      videoTouchVideo: readShortcodeAttribute(attributes, 'videoTouchVideo'),
      videoTouchThumbnail: readShortcodeAttribute(attributes, 'videoTouchThumbnail'),
    })
  }

  return normalizeVideoBlockItem({
    type: VIDEO_BLOCK_TYPES.Youtube,
    youtubeId: readShortcodeAttribute(attributes, 'youtubeId'),
    youtubeVideo: readShortcodeAttribute(attributes, 'youtubeVideo'),
    youtubeThumbnail: readShortcodeAttribute(attributes, 'youtubeThumbnail'),
  })
}

function createVideoBlockHtml(item: VideoBlockItem): string {
  return `<video-block data-video="${escapeHtmlAttribute(
    encodeVideoBlockItem(item)
  )}"></video-block>`
}

function readShortcodeAttribute(attributes: string, name: string): string | undefined {
  const pattern = new RegExp(`${name}="((?:\\\\.|[^"\\\\])*)"`)
  const matched = pattern.exec(attributes)
  return matched ? unescapeShortcodeAttribute(matched[1]) : undefined
}

function readHtmlAttribute(attributes: string): string | undefined {
  const matched = /data-video=(?:"([^"]*)"|'([^']*)'|([^\s>]+))/.exec(attributes)
  return matched?.[1] ?? matched?.[2] ?? matched?.[3]
}

function escapeShortcodeAttribute(value: string): string {
  return value.replace(/\\/g, '\\\\').replace(/"/g, '\\"')
}

function unescapeShortcodeAttribute(value: string): string {
  return value.replace(/\\"/g, '"').replace(/\\\\/g, '\\')
}

function escapeHtmlAttribute(value: string): string {
  return value.replace(/&/g, '&amp;').replace(/"/g, '&quot;')
}

function createMarkdownPlaceholderRegExp(placeholder: string): RegExp {
  return new RegExp(escapeRegExp(placeholder).replace(/_/g, '\\\\?_'), 'g')
}

function escapeRegExp(value: string): string {
  return value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
}

function isVideoBlockItemLike(value: unknown): value is Partial<VideoBlockItem> {
  return typeof value === 'object' && value !== null
}
